import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  addCredentialName,
  deleteCredentialName,
  editCredentialName,
  useGetCredentialNameListByOwnerId,
} from "repositories/licensing-credentials-repository";
import {
  CREDENTIAL_NAME_MAX_LENGTH,
  CREDENTIAL_NAME_SORT,
  getCredentialTypeLabel,
} from "features/credentials/credentialTypes";
import {
  extractCredentialMasterFromResponse,
  normalizeCredentialMasterList,
} from "features/credentials/utils/normalizeCredentialMaster";

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Search, sort, selection, and CRUD for the credential-name picker.
 * @param {{
 *  ownerId: number|string,
 *  isOpen: boolean,
 *  selectedCredential: { id?: number|string, credentialName?: string, type?: number }|null,
 *  t: (key: string, options?: object) => string,
 *  onCredentialUpdated?: (item: object) => void,
 *  onCredentialDeleted?: (id: number|string) => void,
 *  isFromProfile?: boolean,
 * }} params
 */
export function useSelectCredentialName({
  ownerId,
  isOpen,
  selectedCredential,
  t,
  onCredentialUpdated,
  onCredentialDeleted,
  isFromProfile = false,
}) {
  const [selectedVal, setSelectedVal] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState(CREDENTIAL_NAME_SORT.NAME);
  const [isDescending, setIsDescending] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("");
  const [formErrors, setFormErrors] = useState({ name: "", type: "" });
  const [selectError, setSelectError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const searchTimeoutRef = useRef(null);
  const pendingSelectIdRef = useRef(null);
  const pendingSelectNameRef = useRef("");
  const [scrollToId, setScrollToId] = useState(null);

  const { data, isLoading, isFetching, refetch } =
    useGetCredentialNameListByOwnerId(ownerId, "", {
      enabled: isOpen && !!ownerId,
    });

  const credentialList = useMemo(
    () => normalizeCredentialMasterList(data),
    [data],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchInput("");
      setSearchTerm("");
      setSortBy(CREDENTIAL_NAME_SORT.NAME);
      setIsDescending(false);
      setIsFormOpen(false);
      setEditingItem(null);
      setFormName("");
      setFormType("");
      setFormErrors({ name: "", type: "" });
      setSelectError("");
      setCredentialToDelete(null);
      pendingSelectIdRef.current = null;
      pendingSelectNameRef.current = "";
      setScrollToId(null);
      return;
    }

    setSelectedVal(selectedCredential?.id ? selectedCredential : null);
    // Only re-sync the radio when the modal opens so a newly added name stays selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    if (!credentialList.length) {
      return;
    }
    if (pendingSelectIdRef.current) {
      const foundById = credentialList.find(
        (item) => String(item.id) === String(pendingSelectIdRef.current),
      );
      if (foundById) {
        setSelectedVal(foundById);
        pendingSelectIdRef.current = null;
        pendingSelectNameRef.current = "";
        setScrollToId(foundById.id);
        return;
      }
    }
    if (pendingSelectNameRef.current) {
      const foundByName = credentialList.find(
        (item) =>
          (item.credentialName || "").toLowerCase() ===
          pendingSelectNameRef.current.toLowerCase(),
      );
      if (foundByName) {
        setSelectedVal(foundByName);
        pendingSelectNameRef.current = "";
        setScrollToId(foundByName.id);
      }
    }
  }, [credentialList]);

  const filteredList = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const list = term
      ? credentialList.filter((item) =>
          (item.credentialName || "").toLowerCase().includes(term),
        )
      : [...credentialList];

    list.sort((a, b) => {
      const valueA =
        sortBy === CREDENTIAL_NAME_SORT.TYPE
          ? getCredentialTypeLabel(a.type, t).toLowerCase()
          : (a.credentialName || "").toLowerCase();
      const valueB =
        sortBy === CREDENTIAL_NAME_SORT.TYPE
          ? getCredentialTypeLabel(b.type, t).toLowerCase()
          : (b.credentialName || "").toLowerCase();
      const comparison = valueA.localeCompare(valueB);
      return isDescending ? -comparison : comparison;
    });

    return list;
  }, [credentialList, searchTerm, sortBy, isDescending, t]);

  const handleSort = useCallback((column) => {
    setSortBy((prev) => {
      if (prev === column) {
        setIsDescending((descending) => !descending);
        return prev;
      }
      setIsDescending(false);
      return column;
    });
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormName("");
    setFormType("");
    setFormErrors({ name: "", type: "" });
  }, []);

  const openAddForm = useCallback(() => {
    setEditingItem(null);
    setFormName("");
    setFormType("");
    setFormErrors({ name: "", type: "" });
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((item) => {
    setEditingItem(item);
    setFormName(item.credentialName || "");
    setFormType(item.type);
    setFormErrors({ name: "", type: "" });
    setIsFormOpen(true);
  }, []);

  const validateForm = useCallback(() => {
    const name = formName.trim();
    const errors = { name: "", type: "" };

    if (!name) {
      errors.name = t("accountOwner.pleaseAddCredentialName");
    } else if (name.length > CREDENTIAL_NAME_MAX_LENGTH) {
      errors.name = t("form.errors.maxLimit", {
        limit: CREDENTIAL_NAME_MAX_LENGTH,
      });
    }

    if (!formType) {
      errors.type = t("accountOwner.pleaseSelectAtLeastType");
    }

    setFormErrors(errors);

    if (errors.name || errors.type) {
      return null;
    }

    return { name, type: formType };
  }, [formName, formType, t]);

  const handleSaveForm = useCallback(async () => {
    const values = validateForm();
    if (!values) {
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        await editCredentialName({
          Id: editingItem.id,
          CredentialName: values.name,
          Type: values.type,
          OwnerId: ownerId,
          IsFromProfile: isFromProfile,
        });
        const updated = {
          ...editingItem,
          credentialName: values.name,
          type: Number(values.type),
        };
        setSelectedVal((prev) =>
          prev && String(prev.id) === String(editingItem.id) ? updated : prev,
        );
        onCredentialUpdated?.(updated);
        toast.success(t("accountOwner.credentialNameUpdatedSuccessfully"));
      } else {
        const response = await addCredentialName({
          CredentialName: values.name,
          Type: values.type,
          OwnerId: ownerId,
          IsFromProfile: isFromProfile,
        });
        const created = extractCredentialMasterFromResponse(response);
        if (created?.id) {
          pendingSelectIdRef.current = created.id;
          setSelectedVal(created);
          setScrollToId(created.id);
        } else {
          pendingSelectNameRef.current = values.name;
        }
        setSearchInput("");
        setSearchTerm("");
        toast.success(t("accountOwner.credentialNameAddedSuccessfully"));
      }
      closeForm();
      await refetch();
    } catch (error) {
      toast.error(error?.message || t("somethingWentWrong"));
    } finally {
      setIsSaving(false);
    }
  }, [
    closeForm,
    editingItem,
    isFromProfile,
    onCredentialUpdated,
    ownerId,
    refetch,
    t,
    validateForm,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    if (!credentialToDelete) {
      return;
    }
    setIsSaving(true);
    try {
      await deleteCredentialName({
        Id: credentialToDelete.id,
        OwnerId: ownerId,
      });
      if (String(selectedVal?.id) === String(credentialToDelete.id)) {
        setSelectedVal(null);
      }
      onCredentialDeleted?.(credentialToDelete.id);
      toast.success(t("accountOwner.credentialNameDeletedSuccessfully"));
      setCredentialToDelete(null);
      await refetch();
    } catch (error) {
      toast.error(error?.message || t("somethingWentWrong"));
    } finally {
      setIsSaving(false);
    }
  }, [
    credentialToDelete,
    onCredentialDeleted,
    ownerId,
    refetch,
    selectedVal?.id,
    t,
  ]);

  const handleSelectCredential = useCallback((item) => {
    setSelectedVal(item);
    setSelectError("");
  }, []);

  const handleApply = useCallback(() => {
    if (!selectedVal?.id) {
      setSelectError(t("accountOwner.pleaseAddCredentialName"));
      return null;
    }
    setSelectError("");
    return selectedVal;
  }, [selectedVal, t]);

  const clearScrollToId = useCallback(() => {
    setScrollToId(null);
  }, []);

  return {
    selectedVal,
    searchInput,
    setSearchInput,
    sortBy,
    isDescending,
    handleSort,
    filteredList,
    isLoading: isLoading || isFetching || isSaving,
    isFormOpen,
    editingItem,
    formName,
    setFormName,
    formType,
    setFormType,
    formErrors,
    setFormErrors,
    selectError,
    openAddForm,
    openEditForm,
    closeForm,
    handleSaveForm,
    credentialToDelete,
    setCredentialToDelete,
    handleConfirmDelete,
    handleSelectCredential,
    handleApply,
    scrollToId,
    clearScrollToId,
  };
}
